import io from 'socket.io-client';

/**
 * Socket.IO Service
 * Handles real-time WebSocket connections and events for live messaging
 */

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = {};
  }

  /**
   * Connect to WebSocket server
   */
  connect(serverUrl = process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '')) {
    if (this.socket?.connected) {
      console.log('✅ Socket already connected');
      return;
    }

    try {
      const url = serverUrl || 'http://localhost:5000';
      
      this.socket = io(url, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ['websocket', 'polling']
      });

      // Connection events
      this.socket.on('connect', () => {
        console.log('📡 Socket connected:', this.socket.id);
        this.isConnected = true;
        this.emit('socket_connected', { socketId: this.socket.id });
      });

      this.socket.on('disconnect', () => {
        console.log('📴 Socket disconnected');
        this.isConnected = false;
        this.emit('socket_disconnected');
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        this.emit('socket_error', { error });
      });

      // Message events
      this.socket.on('new_message', (data) => {
        console.log('📨 New message received:', data);
        this.emit('new_message', data);
      });

      // Status update events
      this.socket.on('message_status_update', (data) => {
        console.log('📌 Message status updated:', data);
        this.emit('message_status_update', data);
      });

      // Typing indicator events
      this.socket.on('user_typing', (data) => {
        this.emit('user_typing', data);
      });

      this.socket.on('user_stopped_typing', (data) => {
        this.emit('user_stopped_typing', data);
      });

      console.log('🔌 Socket service initialized');
    } catch (error) {
      console.error('Error connecting to socket:', error);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
      console.log('🔌 Socket disconnected');
    }
  }

  /**
   * Join a conversation room for real-time updates
   */
  joinConversation(conversationId) {
    if (!this.socket) {
      console.warn('Socket not connected. Please connect first.');
      return;
    }

    this.socket.emit('join_conversation', conversationId);
    console.log(`✅ Joined conversation room: ${conversationId}`);
  }

  /**
   * Leave a conversation room
   */
  leaveConversation(conversationId) {
    if (!this.socket) {
      console.warn('Socket not connected');
      return;
    }

    this.socket.emit('leave_conversation', conversationId);
    console.log(`❌ Left conversation room: ${conversationId}`);
  }

  /**
   * Send typing indicator
   */
  sendTypingIndicator(conversationId) {
    if (!this.socket) {
      console.warn('Socket not connected');
      return;
    }

    this.socket.emit('typing', { conversationId });
  }

  /**
   * Send stopped typing indicator
   */
  sendStoppedTypingIndicator(conversationId) {
    if (!this.socket) {
      console.warn('Socket not connected');
      return;
    }

    this.socket.emit('stopped_typing', { conversationId });
  }

  /**
   * Register event listener
   */
  on(eventName, callback) {
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = [];
    }
    this.listeners[eventName].push(callback);

    // Also listen via socket if connected
    if (this.socket && eventName !== 'socket_connected' && eventName !== 'socket_disconnected' && eventName !== 'socket_error') {
      // Note: socket.on is already handling these events in connect()
    }
  }

  /**
   * Emit event to listeners
   */
  emit(eventName, data) {
    if (this.listeners[eventName]) {
      this.listeners[eventName].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in listener for ${eventName}:`, error);
        }
      });
    }
  }

  /**
   * Remove event listener
   */
  off(eventName, callback) {
    if (this.listeners[eventName]) {
      this.listeners[eventName] = this.listeners[eventName].filter(cb => cb !== callback);
    }
  }

  /**
   * Get socket connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id || null
    };
  }
}

// Export singleton instance
export default new SocketService();
